import { useState, useEffect, useCallback } from "react";
import { Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";

interface FunnelOption {
  id: string;
  name: string;
  integration_id?: string | null;
}

interface StageOption {
  id: string;
  name: string;
  slug: string;
  order_index: number;
}

interface AutomationFunnelStageSelectorProps {
  workspaceId: string;
  selectedFunnelId?: string;
  selectedFunnelName?: string;
  selectedStageId?: string;
  selectedStageName?: string;
  onSelect: (data: {
    funnel_id: string;
    funnel_name: string;
    stage_id: string;
    stage_name: string;
    stage_slug: string;
  }) => void;
  /** Quando true, permite filtrar só pelo funil (todas as colunas) sem etapa obrigatória. */
  stageOptional?: boolean;
  onFunnelOnlySelect?: (data: { funnel_id: string; funnel_name: string }) => void;
  showFunnelLabel?: string;
  showStageLabel?: string;
  allStagesLabel?: string;
}

export function AutomationFunnelStageSelector({
  workspaceId,
  selectedFunnelId,
  selectedFunnelName,
  selectedStageId,
  selectedStageName,
  onSelect,
  stageOptional = false,
  onFunnelOnlySelect,
  showFunnelLabel = "Funil",
  showStageLabel = "Etapa",
  allStagesLabel = "Todas as colunas",
}: AutomationFunnelStageSelectorProps) {
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);
  const [funnels, setFunnels] = useState<FunnelOption[]>([]);
  const [stages, setStages] = useState<StageOption[]>([]);
  const [loadingFunnels, setLoadingFunnels] = useState(true);
  const [loadingStages, setLoadingStages] = useState(false);
  const [activeFunnelId, setActiveFunnelId] = useState(selectedFunnelId || "");

  const fetchFunnels = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingFunnels(true);
    const { data } = await supabase
      .from("sales_funnels")
      .select("id, name, integration_id")
      .eq("workspace_id", workspaceId)
      .order("order_index");
    setFunnels(data || []);
    setLoadingFunnels(false);
  }, [workspaceId]);

  const fetchStages = useCallback(async (funnelId: string) => {
    if (!funnelId) { setStages([]); return; }
    setLoadingStages(true);
    const { data } = await supabase
      .from("lead_stages")
      .select("id, name, slug, order_index")
      .eq("funnel_id", funnelId)
      .order("order_index");
    setStages(data || []);
    setLoadingStages(false);
  }, []);

  useEffect(() => { fetchFunnels(); }, [fetchFunnels]);

  useEffect(() => {
    if (selectedFunnelId) setActiveFunnelId(selectedFunnelId);
  }, [selectedFunnelId]);

  useEffect(() => {
    if (activeFunnelId) fetchStages(activeFunnelId);
  }, [activeFunnelId, fetchStages]);

  const handleFunnelSelect = (funnelId: string) => {
    const funnel = funnels.find((f) => f.id === funnelId);
    if (!funnel) return;
    setActiveFunnelId(funnel.id);
    setStages([]);
    if (stageOptional && onFunnelOnlySelect) {
      onFunnelOnlySelect({
        funnel_id: funnel.id,
        funnel_name: funnel.name,
      });
    }
  };

  const handleAllStagesSelect = () => {
    const funnel = funnels.find((f) => f.id === activeFunnelId);
    if (!funnel || !onFunnelOnlySelect) return;
    onFunnelOnlySelect({
      funnel_id: funnel.id,
      funnel_name: funnel.name,
    });
  };

  const handleStageSelect = (stageId: string) => {
    if (stageId === "__all__") {
      handleAllStagesSelect();
      return;
    }
    const funnel = funnels.find((f) => f.id === activeFunnelId);
    const stage = stages.find((s) => s.id === stageId);
    if (!funnel || !stage) return;
    onSelect({
      funnel_id: funnel.id,
      funnel_name: funnel.name,
      stage_id: stage.id,
      stage_name: stage.name,
      stage_slug: stage.slug,
    });
  };

  if (loadingFunnels) {
    return (
      <div className="space-y-2">
        <div className="h-9 bg-muted rounded-md animate-pulse" />
        <div className="h-9 bg-muted rounded-md animate-pulse" />
      </div>
    );
  }

  const selectedFunnel = funnels.find(f => f.id === activeFunnelId);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>{showFunnelLabel}</Label>
        <Select value={activeFunnelId || undefined} onValueChange={handleFunnelSelect}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione um funil...">
              {selectedFunnel
                ? resolveFunnelDisplayName(selectedFunnel, integrationById)
                : undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {funnels.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Nenhum funil encontrado</p>
            ) : funnels.map((funnel) => (
              <SelectItem key={funnel.id} value={funnel.id}>
                {resolveFunnelDisplayName(funnel, integrationById)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {activeFunnelId && (
        <div className="space-y-1.5">
          <Label>{showStageLabel}</Label>
          {loadingStages ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando etapas...
            </div>
          ) : (
            <Select
              value={
                selectedStageId ||
                (stageOptional && selectedFunnelId && !selectedStageId ? "__all__" : undefined)
              }
              onValueChange={handleStageSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma etapa..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {stageOptional && onFunnelOnlySelect && (
                  <SelectItem value="__all__">{allStagesLabel}</SelectItem>
                )}
                {stages.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Nenhuma etapa encontrada</p>
                ) : stages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Summary */}
      {selectedFunnelName && (selectedStageName || (stageOptional && !selectedStageId)) && (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-primary/10 text-primary text-sm">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            {selectedStageName
              ? `${selectedFunnelName} → ${selectedStageName}`
              : `${selectedFunnelName} (${allStagesLabel})`}
          </span>
        </div>
      )}
    </div>
  );
}
